import "@/client/libs/dayjs";

import { i18n } from "@lingui/core";
import { detect, fromStorage, fromUrl } from "@lingui/detect-locale";
import { I18nProvider } from "@lingui/react";
import { languages } from "@my-saas/utils";
import { useEffect } from "react";

import { defaultLocale, dynamicActivate } from "../libs/lingui";
import { updateUser } from "../services/user";
import { useAuthStore } from "../stores/auth";

/** Locales whose script reads right-to-left. The HTML `dir` attribute is
 *  flipped to "rtl" when one of these is active — tailwindcss-rtl then auto
 *  mirrors directional utilities (`mr-x` to `ml-x`, `left-x` to `right-x`, etc.).
 */
const RTL_LOCALES = new Set(["he-IL", "ar-SA", "fa-IR", "ur-PK", "yi"]);

export function isRtlLocale(locale: string): boolean {
  return (
    RTL_LOCALES.has(locale) ||
    locale.startsWith("he") ||
    locale.startsWith("ar")
  );
}

function applyHtmlDir(locale: string): void {
  if (typeof document === "undefined") return;
  const dir = isRtlLocale(locale) ? "rtl" : "ltr";
  document.documentElement.setAttribute("dir", dir);
  document.documentElement.setAttribute("lang", locale);
}

type Props = {
  children: React.ReactNode;
};

export const LocaleProvider = ({ children }: Props) => {
  const userLocale = useAuthStore(
    (state) => state.user?.locale ?? defaultLocale,
  );

  useEffect(() => {
    const detectedLocale =
      detect(
        fromUrl("locale"),
        fromStorage("locale"),
        userLocale,
        defaultLocale,
      ) ?? defaultLocale;

    // Activate the locale only if it's supported. Set <html dir/lang> regardless,
    // so tailwindcss-rtl can flip utilities even before the .po file finishes loading.
    if (languages.some((lang) => lang.locale === detectedLocale)) {
      void dynamicActivate(detectedLocale);
      applyHtmlDir(detectedLocale);
    } else {
      void dynamicActivate(defaultLocale);
      applyHtmlDir(defaultLocale);
    }
  }, [userLocale]);

  return <I18nProvider i18n={i18n}>{children}</I18nProvider>;
};

export const changeLanguage = async (locale: string) => {
  window.localStorage.setItem("locale", locale);
  const state = useAuthStore.getState();
  if (state.user) await updateUser({ locale }).catch(() => null);
  // applyHtmlDir runs again on reload through the LocaleProvider effect.
  window.location.reload();
};
